// @flow strict
import * as React from 'react'
import { useState, useReducer } from 'react'
import { Input, Button } from '@mondough/public-web-components'
import { SectionWrapper, SectionThemes } from '../components/SectionWrapper'
import ConsentCheckbox from './ConsentCheckbox'

import styles from './EmailSignUpForm.module.scss'

const API_BASE_PATH = process.env.GATSBY_API_BASE_PATH

const authenticateClientRequest = ({
  clientId: client_id,
  clientSecret: client_secret,
}) =>
  fetch(`${API_BASE_PATH}/oauth2/token`, {
    body: new URLSearchParams({
      grant_type: 'client_credentials',
      client_id,
      client_secret,
    }),
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      Accept: 'application/json',
    },
  })

const postWaitlistSignup = ({ token, name, email, waitlistId: waitlist_id }) =>
  fetch(`${API_BASE_PATH}/hnw-savings/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      name,
      email,
      waitlist_id,
    }),
  })

type FormState = {
  ok: ?boolean,
  message: string,
}

type FormPayload = {
  status: 'error' | 'success',
  message: string,
}

const formStateReducer = (
  state: FormState,
  { status, message }: FormPayload,
): FormState => {
  switch (status) {
    case 'success':
      return {
        ok: true,
        message,
      }
    case 'error':
      return {
        ok: false,
        message,
      }
    default:
      return state
  }
}

export type EmailSignUpFormProps = {
  clientId?: string,
  buttonText?: string,
  clientSecret?: string,
  successMessage?: string,
  titleText?: string,
  userConsentText?: string,
  waitlistId?: string,
}

const EmailSignUpForm = ({
  buttonText = 'Submit',
  clientId,
  clientSecret,
  successMessage = 'Thanks!',
  titleText,
  userConsentText = 'Tick this box to consent to receiving email from Monzo',
  waitlistId,
}: EmailSignUpFormProps) => {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [formState, formStateDispatch] = useReducer(formStateReducer, {
    ok: null,
    message: '',
  })

  const onSubmit = async (evt: SyntheticEvent<HTMLFormElement>) => {
    evt.preventDefault()

    if (clientId != null && clientSecret != null) {
      const errorMessage = 'Sorry, something went wrong'
      try {
        const authenticated = await authenticateClientRequest({
          clientId,
          clientSecret,
        })
        // 4XXs aren't treated as errors so we need to check to see
        // whether the response was OK
        if (!authenticated.ok)
          return formStateDispatch({ status: 'error', message: errorMessage })

        const { access_token: token } = await authenticated.json()
        const signup = await postWaitlistSignup({
          token,
          name,
          email,
          waitlistId,
        })
        // 4XXs aren't treated as errors so we need to check to see
        // whether the response was OK
        if (!signup.ok)
          return formStateDispatch({ status: 'error', message: errorMessage })

        return formStateDispatch({
          status: 'success',
          message: successMessage,
        })
      } catch (err) {
        return formStateDispatch({
          status: 'error',
          message: errorMessage,
        })
      }
    }
  }

  return (
    <SectionWrapper
      theme={SectionThemes.GREY}
      className={styles.section}
      contentClassName={styles.container}
    >
      {formState.ok === true ? (
        <p
          data-testid="EmailSignUpFormSuccessMessage"
          className={styles.success}
          role="alert"
        >
          {formState.message}
        </p>
      ) : (
        <form
          className={styles.form}
          onSubmit={onSubmit}
          data-testid="EmailSignUpForm"
        >
          <fieldset>
            <legend className={styles.subtitle}>{titleText}</legend>
            {formState.ok === false && (
              <p
                data-testid="EmailSignUpFormErrorMessage"
                className={styles.error}
                role="alert"
              >
                {formState.message}
              </p>
            )}
            <ul className={styles.list}>
              <li className={styles.item}>
                <Input
                  name="name"
                  label="Your name"
                  required={true}
                  onChangeHandler={setName}
                  placeholder="Jane Doe"
                />
              </li>
              <li className={styles.item}>
                <Input
                  name="email"
                  label="Email address"
                  type="email"
                  required={true}
                  onChangeHandler={setEmail}
                  placeholder="jane@example.com"
                />
              </li>
              <li className={styles.item}>
                <ConsentCheckbox
                  name="EmailSignUpForm"
                  required
                  text={userConsentText}
                />
              </li>
              <li className={styles.item}>
                <Button type="submit">{buttonText}</Button>
              </li>
            </ul>
          </fieldset>
        </form>
      )}
    </SectionWrapper>
  )
}

export default EmailSignUpForm
